import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Plus, Trash2, X, Archive, FolderOpen } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import ProjectCard from '../components/ProjectCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ProjectsPage — pure grid view of all user projects.
 * Reuses ProjectCard and preserves all handlers from Dashboard.
 * Route: /projects (mounted under DashboardLayout).
 */
const ProjectsPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('active');
  const [selectedIds, setSelectedIds] = useState([]);
  const [renamingId, setRenamingId] = useState(null);
  const [renameText, setRenameText] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  const isSuperadmin = user?.global_role === 'superadmin';

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/projects?include_archived=true`, { headers: { Authorization: `Bearer ${token}` } });
      setProjects(r.data);
      setSelectedIds([]);
    } catch { toast.error('Failed to load projects'); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  const visibleProjects = useMemo(
    () => viewMode === 'archived'
      ? projects.filter(p => p.status === 'archived')
      : projects.filter(p => p.status !== 'archived'),
    [projects, viewMode],
  );

  const handleStartRename = (e, project) => { e.stopPropagation(); setRenamingId(project.id); setRenameText(project.title); };
  const handleSaveRename = async (e, project) => {
    e.stopPropagation();
    const newTitle = renameText.trim();
    if (!newTitle || newTitle === project.title) { setRenamingId(null); return; }
    try {
      const r = await axios.patch(`${API}/projects/${project.id}`, { title: newTitle }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Project renamed');
      setProjects(prev => prev.map(p => p.id === project.id ? { ...p, title: r.data.title } : p));
    } catch (err) { toast.error(err.response?.data?.detail || 'Rename failed'); }
    finally { setRenamingId(null); }
  };
  const handleDeleteProject = async (e, project) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${project.title}"? This permanently removes the script, scenes, all breakdowns, gallery, palettes, budget and snapshots. This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/projects/${project.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Deleted "${project.title}"`);
      setProjects(prev => prev.filter(p => p.id !== project.id));
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete project'); }
  };
  const toggleSelect = (e, pid) => {
    e.stopPropagation();
    setSelectedIds(prev => prev.includes(pid) ? prev.filter(x => x !== pid) : [...prev, pid]);
  };
  const clearSelection = () => setSelectedIds([]);
  const handleBulkArchive = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Archive ${selectedIds.length} project${selectedIds.length === 1 ? '' : 's'}?`)) return;
    setBulkBusy(true);
    try {
      const r = await axios.post(`${API}/projects/bulk-archive`, { project_ids: selectedIds }, { headers: { Authorization: `Bearer ${token}` } });
      const ok = r.data.archived?.length || 0;
      if (ok) toast.success(`${ok} project${ok === 1 ? '' : 's'} archived`);
      await fetchProjects();
    } catch (err) { toast.error(err.response?.data?.detail || 'Bulk archive failed'); }
    finally { setBulkBusy(false); }
  };
  const handleBulkDeleteConfirm = async () => {
    if (deleteConfirmText !== 'DELETE' || selectedIds.length === 0) return;
    setBulkBusy(true);
    try {
      const r = await axios.post(`${API}/projects/bulk-delete`, { project_ids: selectedIds, confirm: true }, { headers: { Authorization: `Bearer ${token}` } });
      const ok = r.data.deleted?.length || 0;
      if (ok) toast.success(`${ok} project${ok === 1 ? '' : 's'} permanently deleted`);
      setShowDeleteModal(false);
      setDeleteConfirmText('');
      await fetchProjects();
    } catch (err) { toast.error(err.response?.data?.detail || 'Bulk delete failed'); }
    finally { setBulkBusy(false); }
  };
  const handleRestore = async (e, project) => {
    e.stopPropagation();
    try {
      await axios.patch(`${API}/projects/${project.id}`, { status: 'active' }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Restored "${project.title}"`);
      await fetchProjects();
    } catch (err) { toast.error(err.response?.data?.detail || 'Restore failed'); }
  };

  const cardProps = (project) => ({
    project, user, viewMode, selectedIds, renamingId, renameText,
    setRenameText, setRenamingId, navigate, toggleSelect,
    handleRestore, handleStartRename, handleDeleteProject, handleSaveRename,
    hasSelection: selectedIds.length > 0,
  });

  return (
    <main className="fp-main" data-testid="projects-page">
      <div className="fp-topbar">
        <div>
          <div className="fp-eyebrow">FiPr.ai Studio</div>
          <h1 className="fp-h1">All Projects</h1>
          <p className="fp-sub">Every production in your workspace — active, archived, and everything in between.</p>
        </div>
      </div>

      <div className="fp-sec-head" style={{ marginTop: 28 }}>
        <div>
          <div className="fp-sec-title">
            <FolderOpen className="h-4 w-4" style={{ color: 'var(--fp-amber)' }} strokeWidth={2} />
            {viewMode === 'archived' ? 'Archived' : 'Active'}
            <span className="chip" data-testid="projects-count-chip">{visibleProjects.length}</span>
          </div>
          <div className="fp-sec-sub">
            {viewMode === 'archived'
              ? 'Restore any production or clear it permanently.'
              : 'Productions currently in motion.'}
          </div>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          {selectedIds.length > 0 && (
            <div className="fp-bulk-bar" data-testid="bulk-action-bar">
              <span className="fp-bulk-count" data-testid="bulk-selected-count">{selectedIds.length} selected</span>
              <button className="fp-bulk-btn fp-bulk-clear" onClick={clearSelection} data-testid="bulk-clear-btn">
                <X className="h-3 w-3" /> Clear
              </button>
              {viewMode === 'active' && (
                <button
                  className="fp-bulk-btn fp-bulk-archive"
                  onClick={handleBulkArchive}
                  disabled={bulkBusy}
                  data-testid="bulk-archive-btn"
                >
                  <Archive className="h-3 w-3" /> Archive ({selectedIds.length})
                </button>
              )}
              {isSuperadmin && (
                <button
                  className="fp-bulk-btn fp-bulk-delete"
                  onClick={() => { setDeleteConfirmText(''); setShowDeleteModal(true); }}
                  disabled={bulkBusy}
                  data-testid="bulk-delete-btn"
                >
                  <Trash2 className="h-3 w-3" /> Delete ({selectedIds.length})
                </button>
              )}
            </div>
          )}
          <div className="fp-segmented" role="tablist">
            {[
              { key: 'active', label: 'Active' },
              { key: 'archived', label: 'Archived' },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setViewMode(key)}
                className={viewMode === key ? 'on' : ''}
                role="tab"
                aria-selected={viewMode === key}
                data-testid={`view-mode-${key}-btn`}
              >
                {label}
              </button>
            ))}
          </div>
          <button
            onClick={() => navigate('/dashboard')}
            className="fp-btn-primary"
            data-testid="new-project-btn"
          >
            <Plus className="h-4 w-4" strokeWidth={2.4} />
            New Project
          </button>
        </div>
      </div>

      {loading ? (
        <div className="fp-grid">
          {[1, 2, 3].map(i => (
            <div key={i} className="animate-pulse" style={{ height: 280, background: 'var(--fp-panel)', border: '1px solid var(--fp-line)', borderRadius: 'var(--fp-r)' }} />
          ))}
        </div>
      ) : visibleProjects.length === 0 ? (
        <div className="fp-empty" data-testid="no-projects-card">
          <FolderOpen className="h-10 w-10 mx-auto mb-3" style={{ color: 'var(--fp-txt-3)' }} strokeWidth={1} />
          <h2 className="text-base font-bold tracking-tight mb-1" style={{ color: 'var(--fp-txt)' }}>
            {viewMode === 'archived' ? 'No archived projects' : 'No active projects yet'}
          </h2>
          <p className="text-sm max-w-sm mx-auto" style={{ color: 'var(--fp-txt-3)' }}>
            {viewMode === 'archived'
              ? 'Archived productions will appear here.'
              : 'Head to the dashboard to spin up your first production.'}
          </p>
        </div>
      ) : (
        <section className="fp-grid" data-testid="projects-grid">
          {visibleProjects.map(project => (
            <ProjectCard key={project.id} {...cardProps(project)} />
          ))}
        </section>
      )}

      <Dialog open={showDeleteModal} onOpenChange={(open) => { if (!bulkBusy) { setShowDeleteModal(open); if (!open) setDeleteConfirmText(''); } }}>
        <DialogContent style={{ background: 'var(--fp-panel)', borderColor: '#dc2626' }} data-testid="bulk-delete-modal">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold tracking-tight flex items-center gap-2" style={{ color: '#dc2626' }}>
              <Trash2 className="h-5 w-5" /> Permanent Delete — {selectedIds.length} project{selectedIds.length === 1 ? '' : 's'}
            </DialogTitle>
            <DialogDescription style={{ color: 'var(--fp-txt-2)' }} className="text-sm">
              This action <strong>cannot be undone</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-3">
            <label className="text-xs uppercase tracking-[0.12em] font-semibold block" style={{ color: 'var(--fp-txt-3)' }}>
              Type <span style={{ color: '#dc2626' }} className="font-mono">DELETE</span> to confirm
            </label>
            <Input
              value={deleteConfirmText}
              onChange={e => setDeleteConfirmText(e.target.value)}
              placeholder="DELETE"
              autoFocus
              style={{ background: 'var(--fp-bg-2)', borderColor: deleteConfirmText === 'DELETE' ? '#dc2626' : 'var(--fp-line)', color: 'var(--fp-txt)' }}
              className="font-mono text-sm focus:ring-0"
              data-testid="bulk-delete-confirm-input"
            />
            <div className="flex gap-2 justify-end pt-2">
              <Button
                onClick={() => { setShowDeleteModal(false); setDeleteConfirmText(''); }}
                disabled={bulkBusy}
                variant="outline"
                style={{ background: 'transparent', borderColor: 'var(--fp-line)', color: 'var(--fp-txt-2)' }}
                data-testid="bulk-delete-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={handleBulkDeleteConfirm}
                disabled={deleteConfirmText !== 'DELETE' || bulkBusy}
                className="font-semibold transition-opacity hover:opacity-90 disabled:opacity-50"
                style={{ background: '#dc2626', color: '#fff' }}
                data-testid="bulk-delete-confirm-btn"
              >
                {bulkBusy ? 'Deleting…' : `Permanently Delete ${selectedIds.length}`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </main>
  );
};

export default ProjectsPage;
